import {
  useEditSubVarietiesMutation,
  useGetSubVarietiesWithVarietiesQuery,
  useGetVarietiesQuery,
} from 'common/api/endpoints/variety';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import { WizardDropdownOption } from 'common/components/WizardControls/WizardDropdown';
import { Variety } from 'common/models';
import * as notifier from 'common/services/notification';
import {
  SubVarietiesTableRows,
  useSubVarietiesTableData,
} from 'features/settings-view/hooks/useSubVarietiesTableData';
import { FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { formatDefaultSubVars, toSubVarietiesDtos } from '../utils/functions';
import { AddSubVariety } from './AddSubVariety';

export type SubVarietiesValues = Record<
  string,
  WizardDropdownOption<Variety> | null
>;

export const SubVarieties: FC = () => {
  const {
    data: varieties,
    isLoading: isLoadingVars,
    isFetching: isFetchingVars,
    error: varsError,
  } = useGetVarietiesQuery();
  const {
    data: subVarieties,
    isLoading: isLoadingSubVars,
    isFetching: isFetchingSubVars,
    error: subVarsError,
  } = useGetSubVarietiesWithVarietiesQuery();
  const [editSubVarieties, { isLoading: isSubmitting }] =
    useEditSubVarietiesMutation();

  const options = useMemo(
    () =>
      (varieties || []).reduce(
        (acc, vrty) => {
          if (vrty.category === 'Conventional') {
            acc.regVarOptions.push({
              label: vrty.varietyCode,
              value: vrty,
            });
          } else if (vrty.category === 'Organic') {
            acc.orgVarOptions.push({
              label: vrty.varietyCode,
              value: vrty,
            });
          }
          return acc;
        },
        { regVarOptions: [], orgVarOptions: [] } as {
          regVarOptions: WizardDropdownOption<Variety>[];
          orgVarOptions: WizardDropdownOption<Variety>[];
        },
      ),
    [varieties],
  );

  const methods = useForm<SubVarietiesValues>({
    values: formatDefaultSubVars(subVarieties || []),
    mode: 'all',
  });
  const {
    control,
    reset,
    formState: { isDirty },
    getFieldState,
  } = methods;

  const { columns, data, isEditing, setIsEditing } = useSubVarietiesTableData(
    subVarieties || [],
    control,
    options,
    isLoadingVars || isFetchingVars,
    isSubmitting,
  );

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: SubVarietiesValues): Promise<void> => {
    if (!isDirty) {
      closeForm();
      return;
    }

    const dtos = toSubVarietiesDtos(data, getFieldState);

    try {
      await editSubVarieties(dtos).unwrap();
      notifier.showSuccessMessage('Sub-varieties updated successfully.');
      closeForm();
    } catch (err) {
      handleError(err, 'Failed to update sub-varieties.');
    }
  };

  const tableProps: DataTableProps<SubVarietiesTableRows, SubVarietiesValues> =
    {
      columns,
      data,
      isLoading: isLoadingSubVars,
      isFetching: isFetchingSubVars,
      maxTableWidth: '900px',
      formProps: {
        methods,
        onSubmit,
        onCancel: closeForm,
        isEditing,
        setIsEditing,
        isDirty,
        isSubmitting,
      },
      FooterComponent: isEditing
        ? undefined
        : () => (
            <AddSubVariety
              regVarOptions={options.regVarOptions}
              orgVarOptions={options.orgVarOptions}
              isLoading={isLoadingVars || isFetchingVars}
            />
          ),
    };

  useEffect(() => {
    if (varsError) {
      handleError(varsError, 'Unable to fetch varieties.');
    }
  }, [varsError]);

  useEffect(() => {
    if (subVarsError) {
      handleError(subVarsError, 'Unable to fetch sub-varieties.');
    }
  }, [subVarsError]);

  return <DataTable {...tableProps} />;
};
